import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { BlogPost } from '../types';
import BlogCard from './BlogCard';
import { getBio, fetchUserBlogs } from '../services/api';

const Profile: React.FC = () => {
  const { user, logout, isLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'posts' | 'about'>('posts');
  const [postType, setPostType] = useState<'published' | 'drafts' | 'archived'>('published');

  const [postsLoading, setPostsLoading] = useState(false);
  const [userBio, setUserBio] = useState<BlogPost | null>(null);
  const [bioLoading, setBioLoading] = useState(false);

  const [userPosts, setUserPosts] = useState<BlogPost[]>([]);
  const [draftPosts, setDraftPosts] = useState<BlogPost[]>([]);
  const [archivedPosts, setArchivedPosts] = useState<BlogPost[]>([]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Load user's blog posts
  useEffect(() => {
    const loadUserPosts = async () => {
      if (!user) return;
      try {
        setPostsLoading(true);
        const publishedBlogs = await fetchUserBlogs(user.username);
        setUserPosts(publishedBlogs);

        const draftBlogs = await fetchUserBlogs(user.username, 'draft');
        setDraftPosts(draftBlogs);

        const archivedPosts = await fetchUserBlogs(user.username, 'archived');
        setArchivedPosts(archivedPosts);
      } catch (error) {
        setUserPosts([]);
      } finally {
        setPostsLoading(false);
      }
    };

    loadUserPosts();
  }, [user]);

  // Load user's Bio Blog
  useEffect(() => {
    const loadUserBio = async () => {
      if (!user) return;

      try {
        setBioLoading(true);
        const bioBlog = await getBio(user.username);
        if (bioBlog) {
          setUserBio(bioBlog);
        } else {
          // If no bio blog exists, set a prompt to edit
          setUserBio({
            id: user.bio_id,
            content: '## Create your bio!',
            author_username: user.username,
            posted_at: '',
          });
        }
      } catch (error) {
        setUserBio({
          id: user.bio_id,
          content: 'Create your bio!',
          author_username: user.username,
          posted_at: '',
        });
      } finally {
        setBioLoading(false);
      }
    };

    loadUserBio();
  }, [user]);

  if (isLoading) {
    return (
      <div className="profile-page">
        <div className="profile-content">
          <div className="loading-state">Loading your profile...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="profile-page minimal-profile">
      <div className="profile-header minimal-header">
        <div className="header-top">
          <div className="profile-info-left">
            <h1>{user.name}</h1>
            <div className="username">@{user.username}</div>
            <div className="username">{user.quote}</div>
          </div>
          <button className="logout-button-header" onClick={handleLogout}>
            Logout
          </button>
        </div>
        <div className="profile-actions">
          <button
            className="new-post-btn"
            onClick={() => navigate('/editor')}
          >
            New Post
          </button>
        </div>
      </div>

      <div className="profile-tabs">
        <div className="tabs-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <button
              className={`tab-button ${activeTab === 'posts' ? 'active-tab' : ''}`}
              onClick={() => setActiveTab('posts')}
            >Posts</button>
            <button
              className={`tab-button ${activeTab === 'about' ? 'active-tab' : ''}`}
              onClick={() => setActiveTab('about')}
            >About</button>
          </div>
          {activeTab === 'posts' ? (
            <div className="post-type-tabs" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <button
                className={`tab-button${postType === 'published' ? ' active-tab' : ''}`}
                onClick={() => setPostType('published')}
              >Published</button>
              <button
                className={`tab-button${postType === 'drafts' ? ' active-tab' : ''}`}
                onClick={() => setPostType('drafts')}
              >Drafts</button>
              <button
                className={`tab-button${postType === 'archived' ? ' active-tab' : ''}`}
                onClick={() => setPostType('archived')}
              >Archived</button>
            </div>
          ) : (
            <button
              className="btn btn-secondary edit-about-btn-header"
              onClick={() => userBio && navigate(`/bio/${userBio.id}`)}
            >
              Edit About
            </button>
          )}
        </div>
        <div className="tabs-underline"></div>
      </div>

      <div className="profile-content minimal-content">
        {activeTab === 'posts' ? (
          <main className="posts-section minimal-posts">
            {postsLoading ? (
              <div className="loading-state">Loading your posts...</div>
            ) : (
              (() => {
                let postsToShow: BlogPost[] = [];
                if (postType === 'published') postsToShow = userPosts;
                else if (postType === 'drafts') postsToShow = draftPosts;
                else if (postType === 'archived') postsToShow = archivedPosts;

                if (postsToShow.length === 0) {
                  return (
                    <div className="no-posts">
                      <p>
                        {postType === 'published' && "You haven't published any posts yet."}
                        {postType === 'drafts' && "No drafts yet."}
                        {postType === 'archived' && "No archived posts yet."}
                      </p>
                    </div>
                  );
                }

                return (
                  <>
                    {/* Big first post (featured) */}
                    <div className="featured-post-container">
                      <BlogCard
                        key={postsToShow[0].id}
                        post={postsToShow[0]}
                        onClick={() => navigate(`/editor/${postsToShow[0].id}`)}
                      />
                    </div>
                    {/* Remaining posts */}
                    {postsToShow.length > 1 && (
                      <div className="posts-grid">
                        {postsToShow.slice(1).map(post => (
                          <BlogCard
                            key={post.id}
                            post={post}
                            onClick={() => navigate(`/editor/${post.id}`)}
                          />
                        ))}
                      </div>
                    )}
                  </>
                );
              })()
            )}
          </main>
        ) : (
          <section className="about-section minimal-about">
            {bioLoading ? (
              <div className="loading-state">Loading...</div>
            ) : (
              <div className="article-body">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeSanitize]}
                >
                  {userBio?.content || 'No bio yet. Tell others about yourself!'}
                </ReactMarkdown>
              </div>
            )}
            
          </section>
        )}
      </div>
    </div>
  );
};

export default Profile;